import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {billingPlatformApi} from 'api';
import {
    Wizard, DataWrapper, MaxUsersStep, BillingAddressStep, ChargebeePaymentStep,
    PaymentSuccessStep, TotalPrice, Button
} from 'components';
import {CHARGEBEE_CLIENT_SCRIPT, MIN_USERS_LIMIT} from 'constants/billing-platform';
import {ACCOUNT_ACTIVATION_WELCOME_PATH} from 'constants/images';
import {AccountStateName, BillingPlatformSubscriptionStatus} from 'enums';
import {useApiData, useExternalScript, useObservable, usePreventWindowUnload} from 'hooks';
import {BaseLayout} from 'layouts/Base';
import {TRANSLATION_KEYS} from 'locale';
import {authDataStore} from 'services';
import {AccountPlanId, BillingAddress, BillingPlatformSubscription} from 'types';

const T_KEYS = TRANSLATION_KEYS.ACCOUNT_ACTIVATION_PAGE;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;
const T_KEYS_WIZARD_NAV = TRANSLATION_KEYS.COMPONENTS.BILLING_PLATFORM_WIZARD_NAV;

enum PageState {
    Initial = 'initial',
    Activation = 'activation'
}

export const AccountActivation: React.FC = () => {
    const {t} = useTranslation();
    const {localUser} = useObservable(authDataStore);
    const [billingAddress, setBillingAddress] = useState<BillingAddress>(null);
    const [maxUsers, setMaxUsers] = useState(MIN_USERS_LIMIT);
    const [loading, setLoading] = useState(true);
    const [pageState, setPageState] = useState<PageState>(PageState.Initial);

    const [wizardInstance, setWizardInstance] = useState(null);
    const paymentCompleted = wizardInstance && wizardInstance.currentStep === wizardInstance.totalSteps;
    usePreventWindowUnload(!paymentCompleted && pageState === PageState.Activation);

    const {
        send: fetchBrowserCredentials,
        data: credentials,
        error: credentialsError,
        reset: resetBrowserCredentials
    } = useApiData(billingPlatformApi.getBrowserCredentials);

    const {loaded: scriptLoaded} = useExternalScript(CHARGEBEE_CLIENT_SCRIPT);

    const handlePageLoaded = useCallback(() => {
        setLoading(false);
    }, []);
    const {send: fetchCustomerInfo, data: customerData, error: customerError} = useApiData(billingPlatformApi.getBillingPlatformCustomer, {
        onSuccess: handlePageLoaded
    });

    const hasValidCustomer = useMemo(() =>
        customerData?.customer && customerData.subscription?.status === BillingPlatformSubscriptionStatus.Future,
    [customerData]);

    const hasError = useMemo(() =>
        !!credentialsError || !!customerError || !hasValidCustomer, [credentialsError, customerError, hasValidCustomer]);

    const showMaxUsersStep = useMemo(() => customerData?.subscription?.planId !== AccountPlanId.Free, [customerData?.subscription?.planId]);

    const navItems = useMemo(() => [
        ...(showMaxUsersStep ? [t(T_KEYS_WIZARD_NAV.MAX_USERS)] : []),
        t(T_KEYS_WIZARD_NAV.BILLING_ADDRESS),
        t(T_KEYS_WIZARD_NAV.CARD_INFO),
        t(T_KEYS_WIZARD_NAV.DONE)
    ], [showMaxUsersStep, t]);

    const {unitPrice, currency, billingPeriodUnit} = useMemo(() =>
        customerData?.subscription || {} as BillingPlatformSubscription, [customerData]);

    const customWizardFooterLabel = useMemo(() => (
        <TotalPrice
            className='sm:tw-mr-4 -sm:tw-w-full -sm:tw-text-right -sm:tw-mb-8'
            unitPrice={unitPrice}
            maxUsers={maxUsers}
            periodUnit={billingPeriodUnit}
            currency={currency}
        />
    ), [currency, maxUsers, unitPrice, billingPeriodUnit]);

    const handleStartJourney = useCallback(() => {
        window.location.reload();
    }, []);

    useEffect(() => {
        if (localUser.accountStateName === AccountStateName.Active) {
            handleStartJourney();
        }
    }, [localUser, handleStartJourney]);

    useEffect(() => {
        if (customerData?.customer.billingAddress) {
            setBillingAddress(customerData.customer.billingAddress);
        }
    }, [customerData]);

    useEffect(() => {
        if (customerData?.subscription.quantity) {
            setMaxUsers(customerData?.subscription.quantity);
        }
    }, [customerData]);

    useEffect(() => {
        if (scriptLoaded) {
            fetchBrowserCredentials();
            fetchCustomerInfo();
            return () => {
                resetBrowserCredentials();
            };
        }
    }, [scriptLoaded, fetchBrowserCredentials, resetBrowserCredentials, fetchCustomerInfo]);

    return (
        <BaseLayout hasRestrictedProfileMenu hasFooter hasLogo>
            <div className='tw-bg-layout tw-h-full'>
                <div className='tw-absolute tw-top-20 tw-bottom-12 tw-left-0 tw-right-0 tw-flex-center'>
                    <DataWrapper
                        isLoading={loading && !hasError}
                        hideContentOnLoading={false}
                        isError={hasError && !loading}
                        errorClassName='tw-px-4 tw-text-center'
                    >
                        <div className='tw-p-8 tw-shadow-md tw-bg-light tw-max-h-full tw-overflow-y-auto'>
                            {pageState === PageState.Initial &&
                                <div className='tw-text-center'>
                                    <img src={ACCOUNT_ACTIVATION_WELCOME_PATH} alt={t(T_KEYS.TITLE)} />
                                    <div className='tw-ts-h3-bold tw-mt-12 tw-mb-8 tw-text-center'>{t(T_KEYS.WELCOME)}</div>
                                    <div className='tw-text-default tw-mb-8'>{t(T_KEYS.INSTRUCTION)}</div>
                                    <Button
                                        rounded
                                        color='primary'
                                        loading={loading}
                                        onClick={() => setPageState(PageState.Activation)}
                                    >
                                        {t(T_KEYS_COMMON.BUTTONS.START)}
                                    </Button>
                                </div>}
                            {pageState === PageState.Activation &&
                                <Fragment>
                                    <div className='tw-ts-h3-bold tw-mb-8 tw-text-center'>{t(T_KEYS.TITLE)}</div>
                                    <Wizard instance={setWizardInstance} navItems={navItems}>
                                        {showMaxUsersStep && <MaxUsersStep
                                            maxUsers={maxUsers}
                                            unitPrice={unitPrice}
                                            currency={currency}
                                            periodUnit={billingPeriodUnit}
                                            wizardFooterProps={{
                                                hasNext: true
                                            }}
                                            onUpdate={setMaxUsers}
                                        />}
                                        <BillingAddressStep
                                            value={billingAddress}
                                            wizardFooterProps={{
                                                hasNext: true,
                                                hasPrevious: true,
                                                children: customWizardFooterLabel
                                            }}
                                            updateCallback={billingPlatformApi.updateBillingAddress}
                                            onSubmit={setBillingAddress}
                                        />
                                        <ChargebeePaymentStep
                                            credentials={credentials}
                                            maxUsers={maxUsers}
                                            unitPrice={unitPrice}
                                            currency={currency}
                                            periodUnit={billingPeriodUnit}
                                            wizardFooterProps={{
                                                hasNext: true,
                                                hasPrevious: true,
                                                children: customWizardFooterLabel
                                            }}
                                            paymentAddedCallback={billingPlatformApi.payUnbilledCharges}
                                        />
                                        <PaymentSuccessStep action={handleStartJourney} />
                                    </Wizard>
                                </Fragment>}
                        </div>
                    </DataWrapper>
                </div>
            </div>
        </BaseLayout>
    );
};